import { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { ComparisonCondition, Condition } from '@the-real-insight/tri-model';

import ConditionSection from '../components/ConditionSection';
import { Button } from '@/components/ui/button';

import { ALERT_TEXT_NAMESPACE } from './alertPlaceholders';
import { Translate } from './AlertActionEditor';
import { deviceAlarmsOf } from './alertDeviceAlarms';
import DeviceAlarmConditions from './DeviceAlarmConditions';
import { conditionsOfRows, MatchMode, viewOfConditions } from './alertConditionModel';
import { noUplinkDays } from './noUplink';
import { summarizeConditions } from './alertRuleSummary';

const TEXT = ALERT_TEXT_NAMESPACE;

/**
 * The conditions of a rule as plain rows: "When [measurement] [below] [10] bar", "and …" / "or …".
 *
 * One switch above two or more rows says whether all or any must hold; the mapping to the stored
 * list (flat = AND, one `OrCondition` = OR) is in `alertConditionModel`. A stored structure the rows
 * cannot show is displayed read-only; nothing is rewritten until the user changes the conditions.
 */
export default function AlertConditionsEditor(properties: {
    t: Translate;
    language: string;
    conditions: any[];
    onChange: (conditions: any[]) => void;
    /** A device alarm was just chosen — the message preview shows that one. */
    onAlarmChosen?: (alarm: string) => void;
}) {
    const { t, conditions } = properties;
    const view = viewOfConditions(conditions);
    // "any" with a single row cannot be read back from the list, so the choice lives here.
    const [chosenMatch, setChosenMatch] = useState<MatchMode | null>(null);
    // "Any of these device alarms" is shown as choices; this switches to the plain rows instead.
    const [showRows, setShowRows] = useState(false);
    const deviceAlarms = deviceAlarmsOf(conditions);

    if (deviceAlarms && !showRows) {
        return (
            <DeviceAlarmConditions
                t={t}
                language={properties.language}
                alarms={deviceAlarms}
                onChange={properties.onChange}
                onAlarmChosen={properties.onAlarmChosen}
                onEditRows={() => setShowRows(true)}
            />
        );
    }

    if (view.kind === 'advanced') {
        const words = { and: t(`${TEXT}summaryAnd`), or: t(`${TEXT}summaryOr`) };

        return (
            <div className="flex min-w-0 flex-col gap-2 rounded-lg border border-dashed bg-muted/40 p-3" role="group" aria-label={t(`${TEXT}advancedRule`)}>
                <p className="text-sm font-medium">{t(`${TEXT}advancedRule`)}</p>
                <p className="min-w-0 break-words text-sm text-muted-foreground">
                    {summarizeConditions(conditions, properties.language, words)}
                </p>
                <div>
                    <Button
                        variant="outline"
                        size="sm"
                        className="gap-2"
                        onClick={() => properties.onChange([])}
                    >
                        <Trash2 aria-hidden className="h-4 w-4" />
                        {t(`${TEXT}advancedRuleRemove`)}
                    </Button>
                </div>
            </div>
        );
    }

    const match: MatchMode = chosenMatch ?? view.match;
    const rows = view.rows;
    const hasNoUplink = noUplinkDays(conditions) !== null;

    const commit = (nextRows: any[], nextMatch: MatchMode) => properties.onChange(conditionsOfRows(nextRows, nextMatch));

    const chooseMatch = (next: MatchMode) => {
        setChosenMatch(next);
        commit(rows, next);
    };

    const word = (index: number): string =>
        index === 0 ? t(`${TEXT}rowWhen`) : match === 'any' ? t(`${TEXT}summaryOr`) : t(`${TEXT}summaryAnd`);

    return (
        <div className="flex min-w-0 flex-col gap-2">
            {rows.length > 1 && !hasNoUplink && (
                <div role="radiogroup" aria-label={t(`${TEXT}matchLabel`)} className="inline-flex max-w-full flex-wrap gap-1 self-start rounded-lg bg-muted p-1">
                    {(['all', 'any'] as MatchMode[]).map((mode) => (
                        <button
                            key={mode}
                            type="button"
                            role="radio"
                            aria-checked={match === mode}
                            className={`rounded-md px-3 py-1 text-sm max-sm:min-h-11 ${match === mode ? 'bg-background font-medium shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                            onClick={() => chooseMatch(mode)}
                        >
                            {t(`${TEXT}match.${mode}`)}
                        </button>
                    ))}
                </div>
            )}
            {rows.map((row, index) => (
                <div key={`row${index}`} className="flex min-w-0 items-start gap-2 rounded-lg border bg-background p-2">
                    <span className="w-9 shrink-0 pt-2 text-sm font-medium text-muted-foreground">{word(index)}</span>
                    <div className="min-w-0 flex-1">
                        <ConditionSection
                            t={t}
                            condition={row}
                            setCondition={(updated: Condition) => {
                                const next = [...rows];

                                next[index] = updated;
                                commit(next, match);
                            }}
                        />
                    </div>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="shrink-0"
                        aria-label={t(`${TEXT}removeCondition`)}
                        title={t(`${TEXT}removeCondition`)}
                        onClick={() => commit(rows.filter((_row, position) => position !== index), match)}
                    >
                        <Trash2 aria-hidden className="h-4 w-4" />
                    </Button>
                </div>
            ))}
            <div className="flex flex-wrap items-center gap-3">
                <Button
                    data-guide-id="settings.alerts.addCondition"
                    variant="outline"
                    size="sm"
                    className="gap-2 max-sm:w-full"
                    disabled={hasNoUplink}
                    onClick={() => commit([...rows, new ComparisonCondition()], match)}
                >
                    <Plus aria-hidden className="h-4 w-4" />
                    {t(`${TEXT}addCondition`)}
                </Button>
                {rows.length === 0 && (
                    <p className="text-sm text-muted-foreground">{t(`${TEXT}noConditionsHint`)}</p>
                )}
            </div>
        </div>
    );
}
